import { computed } from "vue";
import { useI18n } from "vue-i18n";
import siteBase from "@/data/site-base.json";
import { blogFiles } from "@/data/siteContent";

export interface WorkExperience {
  title: string;
  position: string;
  duration: string;
  description: string[];
}

export interface Project {
  title: string;
  category: string;
  link: string;
  blurb: string;
}

export interface NavPage {
  name: string;
  route: string;
  description: string;
}

const projectLinks = Object.fromEntries(
  siteBase.projects.map((project) => [project.title, project.link]),
);

const navRoutes: Record<string, string> = {
  Home: "/Home",
  Profile: "/Profile",
  Project: "/Project",
  Resume: "/Resume",
  Blog: "/Blog",
};

export const useSiteContent = () => {
  const { tm, t } = useI18n();

  const profile = computed(
    () => tm("site.profile") as { name: string; username: string; tagline: string },
  );
  const education = computed(
    () =>
      tm("site.education") as {
        institution: string;
        years: string;
        degree: string;
      },
  );
  const skills = computed(() => tm("site.skills") as Record<string, string[]>);
  const workExperience = computed(() => tm("site.workExperience") as WorkExperience[]);
  const projects = computed<Project[]>(() => {
    const items = tm("site.projects") as {
      title: string;
      category: string;
      blurb: string;
    }[];
    return items.map((project) => ({
      ...project,
      link: projectLinks[project.title] || "#",
    }));
  });
  const navPages = computed<NavPage[]>(() => {
    const pages = tm("site.navPages") as { name: string; description: string }[];
    return pages.map((page) => ({
      ...page,
      route: navRoutes[page.name] || `/${page.name}`,
    }));
  });
  const cvSummary = computed(() => t("site.cvSummary"));

  return {
    profile,
    education,
    skills,
    workExperience,
    projects,
    navPages,
    cvSummary,
    blogFiles,
  };
};
